// ============================================================================
// offlineKezelo.js — az offline pillanatkép-tár KEZELŐJE (2026-10-23, docs/OFFLINE_TERV_2026-10-22.md, V1a).
//
// Az offlineTar.js tárára épül; azt dönti el, MIKOR és MIT ment, MIKOR szolgál ki offline adatot, és mikor töröl:
//  - ment: kizárólag a SZERVER által visszaigazolt pillanatképből (a firebaseClient.js figyelői adják át), kizárólag SAJÁT ESZKÖZ módban, kizárólag
//    az offline-olvasásra ENGEDÉLYEZETT szobákból (`offlineOlvasas: true`, az admin állítja szobánként; alapértelmezés: kikapcsolva);
//  - kiszolgál: csak kapcsolat nélkül, csak engedélyezett szobára, csak a mentett, le nem járt rekordot;
//  - töröl: kijelentkezéskor mindent; ha egy szoba kikerül a tag szobái közül vagy az offline engedélye megszűnik, a szoba rekordjait; a lejáratot
//    és a méretkorlátot az offlineTar.js takarítása tartja.
// Közös / kölcsönkapott eszközön SOHA nem ment, és induláskor kiüríti a tárat.
// A "szerveren legutóbb ellenőrizve" idő a legutóbbi szerver által visszaigazolt pillanatkép ideje - NEM a tárból olvasott időbélyeg (titkár #65).
// ============================================================================

import { MAX_UZENET_TEMANKENT, MAX_TEMA_SZOBANKENT } from "./offlineTar.js";

const MENTES_KESLELTETES_MS = 2000;
const META_AZON = "meta";

export class OfflineKezelo {
  /**
   * @param {{tar: import("./offlineTar.js").OfflineTar, halozatVan: () => boolean, most?: () => number, idozito?: {setTimeout: Function, clearTimeout: Function}}} f
   */
  constructor({ tar, halozatVan, most = () => Date.now(), idozito = { setTimeout: (fn, ms) => setTimeout(fn, ms), clearTimeout: (h) => clearTimeout(h) } }) {
    this.tar = tar;
    this.halozatVan = halozatVan;
    this.most = most;
    this.idozito = idozito;
    this.uid = null;
    this.engedett = false; // saját eszköz mód + bejelentkezett felhasználó
    this.engedelyezettSzobak = new Set();
    this.utolsoSzerverOk = null;
    this.figyelok = new Set();
    this.kesleltetett = new Map(); // kulcs -> { handle, fn }
  }

  /** Bejelentkezés után: saját eszköz módban bekapcsol, közös eszközön kiüríti a tárat és kikapcsol. */
  async aktival(uid, mod) {
    this.leallitKesleltetett();
    this.uid = uid || null;
    this.engedelyezettSzobak = new Set();
    this.utolsoSzerverOk = null;
    if (!uid || mod === "kozos") {
      this.engedett = false;
      if (uid && mod === "kozos") await this.tar.torolMindenkiet().catch(() => {});
      this.ertesit();
      return;
    }
    this.engedett = true;
    try {
      const meta = await this.tar.olvas(uid, META_AZON, META_AZON, this.most());
      if (meta && meta.adat && typeof meta.adat.utolsoSzerverOk === "number") this.utolsoSzerverOk = meta.adat.utolsoSzerverOk;
      const szobak = await this.tar.olvas(uid, "szobak", "mind", this.most());
      if (szobak && Array.isArray(szobak.adat)) this.engedelyezettSzobak = new Set(szobak.adat.map((r) => r.roomId));
    } catch (e) {
      /* a tár nem olvasható: csak a mentés hiányzik, a működés nem akad el */
    }
    this.ertesit();
  }

  /** Kijelentkezés: a felhasználó MINDEN offline adata törlődik, a kezelő kikapcsol. */
  async leallit() {
    const uid = this.uid;
    this.leallitKesleltetett();
    this.engedett = false;
    this.uid = null;
    this.engedelyezettSzobak = new Set();
    this.utolsoSzerverOk = null;
    if (uid) await this.tar.torolMindent(uid).catch(() => {});
    this.ertesit();
  }

  leallitKesleltetett() {
    for (const { handle } of this.kesleltetett.values()) this.idozito.clearTimeout(handle);
    this.kesleltetett.clear();
  }

  figyel(cb) {
    this.figyelok.add(cb);
    return () => this.figyelok.delete(cb);
  }

  ertesit() {
    for (const cb of this.figyelok) {
      try { cb(this); } catch (e) { /* a figyelő hibája nem akaszthatja meg a kezelőt */ }
    }
  }

  engedelyezett(roomId) {
    return this.engedett && this.engedelyezettSzobak.has(roomId);
  }

  /** Kapcsolat nélküli kiszolgálás lehetséges-e (kapcsolat hiányzik ÉS a kezelő bekapcsolt)? */
  offlineKiszolgalas() {
    return this.engedett && !this.halozatVan();
  }

  // --- Mentés (csak szerver által visszaigazolt pillanatképből hívandó) ---

  keslelt(kulcs, fn) {
    const elozo = this.kesleltetett.get(kulcs);
    if (elozo) this.idozito.clearTimeout(elozo.handle);
    const handle = this.idozito.setTimeout(() => {
      this.kesleltetett.delete(kulcs);
      Promise.resolve().then(fn).catch(() => {});
    }, MENTES_KESLELTETES_MS);
    this.kesleltetett.set(kulcs, { handle, fn });
  }

  /** Mentés azonnal a függő (késleltetett) írások kiírásával - a teszt és a lapzárás használja. */
  async kiir() {
    const fuggok = [...this.kesleltetett.values()];
    this.leallitKesleltetett();
    for (const { fn } of fuggok) await Promise.resolve().then(fn).catch(() => {});
  }

  szerverOk() {
    this.utolsoSzerverOk = this.most();
    this.keslelt("meta", () => this.tar.ment(this.uid, META_AZON, META_AZON, { utolsoSzerverOk: this.utolsoSzerverOk }, { most: this.most() }));
    this.ertesit();
  }

  /** A tag szobáinak szerver által visszaigazolt listája. Meghatározza az engedélyezett szobákat; kikerült / letiltott szoba rekordjait törli. */
  szobakSzerverrol(rooms) {
    if (!this.engedett) return;
    const uid = this.uid;
    const engedelyezettek = (rooms || []).filter((r) => r && r.offlineOlvasas === true && r.deleting !== true);
    const elozo = this.engedelyezettSzobak;
    this.engedelyezettSzobak = new Set(engedelyezettek.map((r) => r.roomId));
    const kikerult = [...elozo].filter((id) => !this.engedelyezettSzobak.has(id));
    this.szerverOk();
    this.keslelt("szobak", async () => {
      for (const id of kikerult) await this.tar.torolSzoba(uid, id);
      await this.tar.ment(uid, "szobak", "mind", engedelyezettek, { most: this.most() });
    });
  }

  temakSzerverrol(roomId, temak) {
    if (!this.engedelyezett(roomId)) return;
    const uid = this.uid;
    this.szerverOk();
    this.keslelt(`temak|${roomId}`, () => this.tar.ment(uid, "temak", roomId, (temak || []).slice(0, MAX_TEMA_SZOBANKENT), { roomId, most: this.most() }));
  }

  uzenetekSzerverrol(roomId, temaAzon, uzenetek) {
    if (!this.engedelyezett(roomId)) return;
    const uid = this.uid;
    const lista = uzenetek || [];
    this.szerverOk();
    this.keslelt(`uzenetek|${roomId}|${temaAzon}`, () =>
      this.tar.ment(uid, "uzenetek", `${roomId}|${temaAzon || "-"}`, lista.slice(Math.max(0, lista.length - MAX_UZENET_TEMANKENT)), { roomId, most: this.most() })
    );
  }

  /**
   * A belépéshez szükséges két egyszeri olvasás (saját jogosultság: "hozzaferes", saját profil: "profil") mentése: nélkülük az offline hidegindítás
   * elakadna a jogosultság-ellenőrzésnél. Csak szerver által visszaigazolt olvasásból hívandó. A mentett jogosultság a MENTÉS pillanatában érvényes:
   * kapcsolat visszatértekor a program újra ellenőrzi (main.js), és ha a tag már nem aktív, a tár törlődik.
   */
  profilMentes(tipus, adat) {
    if (!this.engedett || !adat) return;
    const uid = this.uid;
    this.keslelt(`profil|${tipus}`, () => this.tar.ment(uid, tipus, "sajat", adat, { most: this.most() }));
  }

  async profilOffline(tipus) {
    if (!this.offlineKiszolgalas()) return null;
    return this.tar.olvas(this.uid, tipus, "sajat", this.most());
  }

  // --- Kiszolgálás (kapcsolat nélkül) ---

  async szobakOffline() {
    if (!this.offlineKiszolgalas()) return null;
    const r = await this.tar.olvas(this.uid, "szobak", "mind", this.most());
    if (!r || !Array.isArray(r.adat)) return null;
    this.engedelyezettSzobak = new Set(r.adat.map((x) => x.roomId));
    return { adat: r.adat, savedAt: r.savedAt };
  }

  async temakOffline(roomId) {
    if (!this.offlineKiszolgalas() || !this.engedelyezett(roomId)) return null;
    return this.tar.olvas(this.uid, "temak", roomId, this.most());
  }

  async uzenetekOffline(roomId, temaAzon) {
    if (!this.offlineKiszolgalas() || !this.engedelyezett(roomId)) return null;
    return this.tar.olvas(this.uid, "uzenetek", `${roomId}|${temaAzon || "-"}`, this.most());
  }

  /** A felületnek: offline van-e (kapcsolat nélkül, bekapcsolt kezelővel), és mikor volt az utolsó szerverrel egyeztetett pillanatkép. */
  allapot() {
    return { offline: this.engedett && !this.halozatVan(), utolsoSzerverOk: this.utolsoSzerverOk, engedett: this.engedett };
  }
}
